import {
    DrawerAction,
    FoodsIcon,
    FoodsTags,
    LinkAction,
    NavbarButtonType,
    NavbarDrawerItemType,
    NavbarLinkType,
    TopLinkAction,
} from "@foodpilot/foods";
import { Badge } from "@mui/material";
import { useMemo } from "react";
import { useLocation, Link } from "react-router";
import { useTranslation } from "react-i18next";
import { useFoodpilotContext } from "src/context/FoodpilotContext.tsx";
import { useNavigationMenu, MenuTreeNode } from "src/api/client-api/menu/useNavigationMenu";
import { useGetAllPuckPages } from "src/api/client-api/cms/useGetAllPuckPages";
import { getPageRoles } from "src/pages/CMS/utils/roles";
import { filterMenusByPermissions, filterMenusByCmsRestrictions, CmsPageRestrictions } from "src/utils/menuPermissions";
import { isMenuItemActive } from "src/utils/activeMenuDetection";
import { CompaniesService } from "src/services/Companies/CompaniesService";
import { CampaignSelector } from "src/components/Selector";
import { resolveMenuIcon } from "src/utils/menuIcon";
import { useSocialUnreadCount } from "src/api/client-api/social/useSocialUnreadCount";

/**
 * New navigation hook based on the dynamic menu system
 * Default and only navigation source
 */
export const useMenuBasedNavigation = () => {
    const renderFoodsIcon = (name?: string) => <FoodsIcon size={2} icon={resolveMenuIcon(name)} />;
    const location = useLocation();
    const { t } = useTranslation();
    const { user, canUser, selectedCompany, currentCampaign } = useFoodpilotContext();
    const { menuTree, isLoading } = useNavigationMenu(selectedCompany?.id);
    const { data: socialUnreadCount = 0 } = useSocialUnreadCount();
    const companyIdToRedirect = selectedCompany !== null ? selectedCompany.id : CompaniesService.getCurrentCompanyId();

    // Fetch all active CMS pages so we can filter sidebar items by role and sector restrictions.
    // CMS page sector restrictions are stored as IRIs (e.g. "/api/organization_sectors/1") and role
    // restrictions as role strings (e.g. "ROLE_VITICULTEUR") on each page.
    const { pages: cmsPages, isLoading: isCmsPagesLoading } = useGetAllPuckPages({
        pageType: "CMS",
        status: "active",
        itemsPerPage: 500,
    });

    const urlToRestrictionsMap = useMemo(() => {
        const map = new Map<string, CmsPageRestrictions>();
        for (const page of cmsPages) {
            if (typeof page.url === "string" && page.url.trim() !== "") {
                const normalized = page.url.startsWith("/") ? page.url : `/${page.url}`;
                map.set(normalized, {
                    sectors: page.sectors ?? [],
                    allowedRoles: getPageRoles(page),
                });
            }
        }
        return map;
    }, [cmsPages]);

    const selectedSectorIri = selectedCompany?.organizationSector?.["@id"] ?? null;
    const userRoles = user.trustedUser.roles;

    const firstButtons: NavbarButtonType[] = [
        {
            type: "custom",
            render: (options) => {
                const { isExpanded, extendSidebar } = options;

                // Used as an anchor for the Tutorial add-on, JIMO
                const htmlId = "campaign-selector-sidebar-id";
                if (isExpanded === false) {
                    return (
                        <div id={htmlId}>
                            <FoodsTags
                                label={currentCampaign?.label ?? ""}
                                variant="tags-white"
                                onClick={extendSidebar}
                            />
                        </div>
                    );
                }
                return (
                    <div id={htmlId}>
                        <CampaignSelector
                            disableText
                            BoxPropsOverride={{
                                width: "100%",
                            }}
                        />
                    </div>
                );
            },
        },
        { type: "separator" },
    ];

    // Convert menu tree to navbar buttons
    const convertMenuToButtons = (nodes: MenuTreeNode[]): NavbarButtonType[] => {
        return nodes.map((node) => convertNodeToButton(node, false)).filter(Boolean) as NavbarButtonType[];
    };

    const resolveUrl = (rawUrl: string): string => {
        if (!rawUrl) return rawUrl;

        // Absolute URLs pointing at the app's own origin should be treated as internal
        // routes so that clicking them uses react-router instead of opening a new tab.
        if (/^https?:\/\//i.test(rawUrl)) {
            try {
                const parsed = new URL(rawUrl);
                if (typeof window !== "undefined" && parsed.origin === window.location.origin) {
                    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
                }
            } catch {
                // fall through - treat as external
            }
            return rawUrl;
        }

        // Non-HTTP external links (mail, tel, protocol-relative): leave as-is
        if (/^(mailto:|tel:|ftp:|\/\/)/i.test(rawUrl)) return rawUrl;

        // Ensure relative paths have a leading slash so react-router treats them as internal
        const withSlash = rawUrl.startsWith("/") ? rawUrl : `/${rawUrl}`;

        // Handle URLs with {companyId} variable
        if (withSlash.includes("{companyId}")) {
            if (companyIdToRedirect == null) return "/";
            return withSlash.replace("{companyId}", companyIdToRedirect.toString());
        }

        return withSlash;
    };

    const containsUrl = (nodes: MenuTreeNode[], target: string): boolean => {
        for (const n of nodes) {
            if (n.item.url === target) return true;
            if (containsUrl(n.children, target)) return true;
        }
        return false;
    };

    const renderMenuIcon = (item: MenuTreeNode["item"], finalUrl: string) => {
        const icon = renderFoodsIcon(item.icon);
        const isSocialMenu = finalUrl === "/social" || item.moduleFeatureKey === "FS-news-feed";

        if (!isSocialMenu || socialUnreadCount <= 0) {
            return icon;
        }

        return (
            <Badge badgeContent={socialUnreadCount} color="error" max={99}>
                {icon}
            </Badge>
        );
    };

    const convertNodeToButton = (node: MenuTreeNode, isMenu: boolean): NavbarButtonType | null => {
        const { item, children } = node;

        // Skip inactive items
        if (!item.isActive) return null;

        // If has children, create a drawer
        if (children.length > 0) {
            const childButtons = children
                .map((child) => convertNodeToButton(child, true))
                .filter(Boolean) as NavbarLinkType[];

            if (childButtons.length === 0) return null; // Empty drawer

            return {
                type: "custom",
                render: (options) => {
                    return (
                        <DrawerAction
                            button={{
                                label: t(item.i18nKey || "", item.label),
                                icon: renderFoodsIcon(item.icon),
                                items: childButtons,
                            }}
                            {...options}
                        />
                    );
                },
            };
        }

        if (item.label === "separator") {
            return { type: "separator" };
        }
        // Single link
        if (!item.url) return null; // Skip items without URL
        const finalUrl = resolveUrl(item.url);

        if (isMenu === true) {
            const drawerButton: NavbarDrawerItemType = {
                type: "custom",
                isSelected: isMenuItemActive(finalUrl, location.pathname),
                render: (options) => {
                    const isExternal = finalUrl.startsWith("/") === false;
                    return (
                        <Link
                            to={finalUrl}
                            target={isExternal ? "_blank" : undefined}
                            style={{
                                textDecoration: "none",
                            }}
                        >
                            <LinkAction
                                button={{
                                    label: t(item.i18nKey || "", item.label),
                                    icon: renderMenuIcon(item, finalUrl),
                                    isSelected: isMenuItemActive(finalUrl, location.pathname),
                                    action: () => {},
                                }}
                                {...options}
                            />
                        </Link>
                    );
                },
            };
            return drawerButton;
        }

        return {
            type: "custom",
            render: (options) => {
                const isExternal = finalUrl.startsWith("/") === false;
                return (
                    <Link
                        to={finalUrl}
                        target={isExternal ? "_blank" : undefined}
                        style={{
                            textDecoration: "none",
                        }}
                    >
                        <TopLinkAction
                            button={{
                                label: t(item.i18nKey || "", item.label),
                                icon: renderMenuIcon(item, finalUrl),
                                isSelected: isMenuItemActive(finalUrl, location.pathname),
                                action: () => {},
                            }}
                            {...options}
                        />
                    </Link>
                );
            },
        };
    };

    // If still loading, return empty array
    if (isLoading || isCmsPagesLoading) {
        return [];
    }

    // Filter menu first by user permissions, then by CMS page restrictions (role + sector).
    const permissionFilteredMenu = filterMenusByPermissions(menuTree, canUser);
    const filteredMenu = filterMenusByCmsRestrictions(
        permissionFilteredMenu,
        selectedSectorIri,
        userRoles,
        urlToRestrictionsMap,
    );

    // Convert to buttons

    const menuButtons = convertMenuToButtons(filteredMenu);

    const allButtons = [...firstButtons, ...menuButtons];
    // Only dynamic menu buttons when the dynamic menu system is active
    return allButtons;
};
